/**
 * My Bag — the page shell, the seed-and-confirm orchestration and the two views
 * (MY_BAG_PLAN.md §2.3, §2.4, §4, §7).
 *
 * Two views of one object: the **Rack** you fill in and the **Gap Map** that
 * draws it. This page owns the ladder. `Rack` and `GapMap` are both controlled
 * and hold no state of their own, so every mutation lands in `slots` below and
 * the analysis is recomputed from that — including before anything has been
 * saved, which is what the `slots`-beats-`bag_id` rule on `POST /bag/analyze`
 * exists for.
 *
 * Findings live on the **map**, not on the Rack: §7.2 routes
 * `/bag?view=map&focus=bends` straight at them, and the Rack's job is input.
 *
 * Three rules from the plan are load-bearing in the render and are not
 * cosmetic:
 *   - the tier is always shown (§3.4) — a tier-3 answer must not be dressed up
 *     as a tier-1 one, and a genuinely new live user arrives at tier 3;
 *   - every bend card prints its `caveat` verbatim (§9.3);
 *   - `blocked_bends` are shown, never swallowed (§2.4) — a bend blocked as
 *     `estimated` is the visible reward for tap-confirming a rung.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import ProfessionalHeader from '../components/ProfessionalHeader';
import Rack from '../components/bag/Rack';
import GapMap from '../components/bag/GapMap';
import {
  AnalysisHeadline,
  BlockedBendCard,
  FindingCard,
  GapCard,
  LONG_IRON_CAVEAT,
  yards,
} from '../components/bag/AnalysisPanels';
import {
  buildGapMapModel,
  slotsWithBendsApplied,
  verdictSentence,
} from '../components/bag/gapMapModel';
import { useAuth } from '../contexts/AuthContext';
import {
  analyzeBag,
  createBag,
  getMeasuredCarries,
  getMyBag,
  replaceSlots,
  seedBag,
} from '../services/bagService';
import { logEvent, newSessionId } from '../services/telemetryService';
import {
  Bag,
  BagAnalysis,
  BagSlot,
  BagSlotUpdate,
  ClubCategory,
  MeasuredCarriesResponse,
} from '../types/bag.types';

type TabKey = 'rack' | 'map';

/**
 * The hand-fill ladder, mirroring `_SEED_TEMPLATE` in
 * `api/v1/endpoints/bag.py`. It exists for the `seedable: false` path — a
 * profile without both anchors is a normal answer, not an error, and the user
 * still gets a ladder to fill rather than a dead end.
 */
const LADDER_TEMPLATE: { slot_order: number; club_label: string; club_category: ClubCategory }[] = [
  { slot_order: 0, club_label: 'Dr', club_category: 'driver' },
  { slot_order: 1, club_label: '3w', club_category: 'wood' },
  { slot_order: 2, club_label: '5w', club_category: 'wood' },
  { slot_order: 3, club_label: '4i', club_category: 'iron' },
  { slot_order: 4, club_label: '5i', club_category: 'iron' },
  { slot_order: 5, club_label: '6i', club_category: 'iron' },
  { slot_order: 6, club_label: '7i', club_category: 'iron' },
  { slot_order: 7, club_label: '8i', club_category: 'iron' },
  { slot_order: 8, club_label: '9i', club_category: 'iron' },
  { slot_order: 9, club_label: 'PW', club_category: 'iron' },
  { slot_order: 10, club_label: 'GW', club_category: 'wedge' },
  { slot_order: 11, club_label: 'SW', club_category: 'wedge' },
  { slot_order: 12, club_label: 'LW', club_category: 'wedge' },
  { slot_order: 13, club_label: 'Putter', club_category: 'putter' },
];

const blankLadder = (): BagSlot[] =>
  LADDER_TEMPLATE.map((t) => ({
    ...t,
    in_bag: true,
    carry_yards: null,
    // Nothing here was seeded, so nothing here is `estimated`. A number the
    // user types into a blank rung is `entered` the moment it exists.
    distance_provenance: 'entered' as const,
  }));

const byOrder = (a: BagSlot, b: BagSlot) => a.slot_order - b.slot_order;

/** Strip the server-assigned keys before a write or an analyze — the ladder we
 *  hold may be unsaved, and a stale `id` is meaningless to both routes. */
const toWire = (slots: BagSlot[]): BagSlot[] =>
  slots.map(({ id, bag_id, ...rest }) => ({
    ...rest,
    carry_yards: rest.club_category === 'putter' ? null : rest.carry_yards ?? null,
    total_yards: rest.club_category === 'putter' ? null : rest.total_yards ?? null,
  }));

const describeError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err) && err.response) {
    const detail = err.response.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const field = Array.isArray(first?.loc) ? first.loc[first.loc.length - 1] : '';
      return `${fallback}: ${field ? `${field} — ` : ''}${first?.msg ?? 'the server rejected the ladder'}.`;
    }
    if (err.response.status === 403) {
      return `${fallback}: this feature is superuser-only while it is in testing.`;
    }
    return `${fallback}: the server returned ${err.response.status}.`;
  }
  return `${fallback}: could not reach the server.`;
};

/** One id per visit, so the map's events group with the Rack's (§6.2, S8). */
const bagSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem('bag_session_id');
    if (existing) return existing;
    const fresh = newSessionId();
    sessionStorage.setItem('bag_session_id', fresh);
    return fresh;
  } catch {
    return newSessionId();
  }
};

// ---------------------------------------------------------------------------

const MyBagPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab: TabKey = searchParams.get('view') === 'map' ? 'map' : 'rack';
  const focus = searchParams.get('focus');

  const [bag, setBag] = useState<Bag | null>(null);
  const [slots, setSlots] = useState<BagSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [seeding, setSeeding] = useState(false);
  const [seedReason, setSeedReason] = useState<string | null>(null);
  const [anchorsUsed, setAnchorsUsed] = useState<string[]>([]);

  /** §2.4's top tier. Fetched once beside the bag; a user with no launch
   *  monitor history gets an empty list and never sees any of this. */
  const [measured, setMeasured] = useState<MeasuredCarriesResponse | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  const [analysis, setAnalysis] = useState<BagAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  /** §4's before/after flip. */
  const [applied, setApplied] = useState(false);
  const [afterAnalysis, setAfterAnalysis] = useState<BagAnalysis | null>(null);
  const [afterError, setAfterError] = useState<string | null>(null);

  const sessionId = useRef<string>(bagSessionId());
  const findingsRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const existing = await getMyBag();
      // `null` at 200 is "no bag yet" — a steady state, not a failure.
      setBag(existing);
      setSlots(existing ? [...existing.slots].sort(byOrder) : []);
      setDirty(false);
    } catch (err) {
      setError(describeError(err, 'Could not load your bag'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Launch-monitor carries, fetched alongside the bag. Deliberately not part
   *  of `load`: an empty table is the normal case today (§6.3), and a failure
   *  here must not take the whole page down with it — the ladder works without
   *  it, it just never reaches the top provenance tier. */
  useEffect(() => {
    let live = true;
    getMeasuredCarries()
      .then((result) => {
        if (live) setMeasured(result);
      })
      .catch(() => {
        if (live) setMeasured(null);
      });
    return () => {
      live = false;
    };
  }, []);

  // --- analysis: debounced, stateless, safe to run on an unsaved ladder ----
  const analyzeSeq = useRef(0);
  const analyzable = useMemo(
    () => slots.filter((s) => s.in_bag && s.carry_yards !== null && s.carry_yards !== undefined).length >= 2,
    [slots]
  );
  const wireSlots = useMemo(() => toWire(slots), [slots]);

  useEffect(() => {
    if (!analyzable) {
      setAnalysis(null);
      setAnalysisError(null);
      return;
    }
    const seq = ++analyzeSeq.current;
    setAnalyzing(true);
    const timer = setTimeout(async () => {
      try {
        const result = await analyzeBag({ slots: wireSlots });
        if (seq !== analyzeSeq.current) return;
        setAnalysis(result);
        setAnalysisError(null);
      } catch (err) {
        if (seq !== analyzeSeq.current) return;
        setAnalysis(null);
        setAnalysisError(describeError(err, 'Could not analyse this ladder'));
      } finally {
        if (seq === analyzeSeq.current) setAnalyzing(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [wireSlots, analyzable]);

  // --- what the Apply toggle has to work with ------------------------------
  const bendLabels = useMemo(() => {
    const out = new Set<string>();
    for (const f of analysis?.findings ?? []) if (f.kind === 'bend') out.add(f.club_label);
    return out;
  }, [analysis]);

  const swapLabels = useMemo(() => {
    const out = new Set<string>();
    for (const f of analysis?.findings ?? []) if (f.kind === 'swap') out.add(f.slot_label);
    return out;
  }, [analysis]);

  const canApply = bendLabels.size > 0 || swapLabels.size > 0;

  /**
   * The after-state is the **server's** answer on the bent ladder, not a local
   * re-colour. §3.5's tolerance is provenance-scaled and lives in
   * `bag_gapping.tol_rung`; copying those thresholds into the frontend to
   * recolour the map would put the plan's least-supported constants in two
   * places. `/bag/analyze` is stateless and takes an unsaved ladder, which is
   * precisely what it was given `slots`-beats-`bag_id` for.
   */
  const projectedSlots = useMemo(
    () => (applied ? toWire(slotsWithBendsApplied(slots, analysis?.projected_carries)) : null),
    [applied, slots, analysis]
  );

  const afterSeq = useRef(0);
  useEffect(() => {
    if (!applied || !analysis) {
      setAfterAnalysis(null);
      setAfterError(null);
      return;
    }
    if (bendLabels.size === 0) {
      // Nothing moves: a swap has no carry to move a rung to (§6.7).
      setAfterAnalysis(analysis);
      setAfterError(null);
      return;
    }
    const seq = ++afterSeq.current;
    (async () => {
      try {
        const result = await analyzeBag({ slots: projectedSlots ?? [] });
        if (seq !== afterSeq.current) return;
        setAfterAnalysis(result);
        setAfterError(null);
      } catch (err) {
        if (seq !== afterSeq.current) return;
        setAfterAnalysis(null);
        setAfterError(describeError(err, 'Could not work out the after picture'));
      }
    })();
  }, [applied, analysis, bendLabels, projectedSlots]);

  const activeAnalysis = applied ? afterAnalysis : analysis;

  const mapModel = useMemo(
    () =>
      buildGapMapModel(slots, activeAnalysis, {
        projected: applied ? analysis?.projected_carries ?? null : null,
        swapLabels,
      }),
    [slots, activeAnalysis, applied, analysis, swapLabels]
  );

  const verdict = useMemo(
    () => verdictSentence(mapModel, activeAnalysis),
    [mapModel, activeAnalysis]
  );

  // --- ladder mutations ----------------------------------------------------
  const handleSlotChange = useCallback((slotOrder: number, patch: BagSlotUpdate) => {
    setDirty(true);
    setSavedAt(null);
    setSlots((prev) =>
      prev.map((s) => (s.slot_order === slotOrder ? ({ ...s, ...patch } as BagSlot) : s))
    );
  }, []);

  /** §4 — a ghosted rung goes back in the bag with one tap. */
  const handleUnbench = useCallback(
    (slotOrder: number) => {
      handleSlotChange(slotOrder, { in_bag: true });
      logEvent({
        session_id: sessionId.current,
        event_type: 'bag_map_unbenched',
        metadata: { slot_order: slotOrder },
      });
    },
    [handleSlotChange]
  );

  /** One tap promotes a seeded rung to `entered` (§2.3 step 3). That promotion
   *  is what unblocks bend advice on it (§2.4). */
  const handleConfirm = useCallback((slotOrder: number) => {
    setDirty(true);
    setSavedAt(null);
    setSlots((prev) =>
      prev.map((s) =>
        s.slot_order === slotOrder && s.distance_provenance === 'estimated'
          ? { ...s, distance_provenance: 'entered' as const }
          : s
      )
    );
  }, []);

  const handleConfirmAll = useCallback(() => {
    setDirty(true);
    setSavedAt(null);
    setSlots((prev) =>
      prev.map((s) =>
        s.distance_provenance === 'estimated' && s.carry_yards !== null && s.carry_yards !== undefined
          ? { ...s, distance_provenance: 'entered' as const }
          : s
      )
    );
  }, []);

  /* Count only rungs a tap can actually promote. The putter is one of the
     fourteen but holds no distance (§4), so it is seeded `estimated` and stays
     that way forever — counting it leaves the banner stuck at "1 rung is still
     estimated" after Confirm all, nagging about something nobody can fix. Same
     predicate as `handleConfirmAll` above, deliberately. */
  const estimatedCount = useMemo(
    () =>
      slots.filter(
        (s) =>
          s.distance_provenance === 'estimated' &&
          s.carry_yards !== null &&
          s.carry_yards !== undefined
      ).length,
    [slots]
  );

  /* Launch-monitor carries this ladder has not taken up yet (§2.4). A rung
     already holding that exact number at `measured` is not pending — otherwise
     the banner never goes away, including straight after a seed, which stamps
     these itself. A club with no rung in the ladder is skipped rather than
     added: the 14-slot budget is the user's to spend (§4). */
  const pendingMeasured = useMemo(() => {
    if (!measured) return [];
    const byLabel = new Map(slots.map((s) => [s.club_label, s]));
    return measured.carries.filter((c) => {
      const slot = byLabel.get(c.club_label);
      if (!slot || slot.club_category === 'putter') return false;
      return !(slot.distance_provenance === 'measured' && slot.carry_yards === c.carry_yards);
    });
  }, [measured, slots]);

  /** Take the launch monitor's number over whatever the rung holds. It is the
   *  one source that outranks a typed number, which is the whole point of the
   *  tier — so this overwrites `entered` rungs too, not just `estimated` ones. */
  const handleApplyMeasured = useCallback(() => {
    if (pendingMeasured.length === 0) return;
    const byLabel = new Map(pendingMeasured.map((c) => [c.club_label, c]));
    setDirty(true);
    setSavedAt(null);
    setSlots((prev) =>
      prev.map((s) => {
        const hit = byLabel.get(s.club_label);
        return hit
          ? { ...s, carry_yards: hit.carry_yards, distance_provenance: 'measured' as const }
          : s;
      })
    );
    logEvent({
      session_id: sessionId.current,
      event_type: 'bag_measured_applied',
      metadata: { labels: pendingMeasured.map((c) => c.club_label) },
    });
  }, [pendingMeasured]);

  // --- seed ----------------------------------------------------------------
  const handleSeed = useCallback(async () => {
    setSeeding(true);
    setSeedReason(null);
    setError(null);
    try {
      const proposal = await seedBag();
      if (!proposal.seedable) {
        // Not an error — a normal answer. Say why, and leave the hand-fill door open.
        setSeedReason(proposal.reason ?? 'we could not seed a ladder from your profile');
        return;
      }
      setSlots([...proposal.slots].sort(byOrder));
      setAnchorsUsed(proposal.anchors_used);
      setDirty(true);
      setSavedAt(null);
    } catch (err) {
      setError(describeError(err, 'Could not seed from your profile'));
    } finally {
      setSeeding(false);
    }
  }, []);

  const handleStartByHand = useCallback(() => {
    setSlots(blankLadder());
    setAnchorsUsed([]);
    setDirty(true);
    setSavedAt(null);
  }, []);

  // --- save ----------------------------------------------------------------
  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = toWire(slots);
      if (bag === null) {
        const created = await createBag({ name: 'My bag', slots: payload });
        setBag(created);
        setSlots([...created.slots].sort(byOrder));
      } else {
        const written = await replaceSlots(bag.id, payload);
        setSlots([...written].sort(byOrder));
      }
      setDirty(false);
      setSavedAt(new Date().toLocaleTimeString());
    } catch (err) {
      setSaveError(describeError(err, 'Could not save your bag'));
    } finally {
      setSaving(false);
    }
  }, [bag, slots]);

  const goToTab = useCallback(
    (next: TabKey) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'map') params.set('view', 'map');
      else {
        params.delete('view');
        params.delete('focus');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleToggleApply = useCallback(() => {
    const next = !applied;
    setApplied(next);
    logEvent({
      session_id: sessionId.current,
      event_type: next ? 'bag_map_recommendations_applied' : 'bag_map_recommendations_reverted',
      metadata: { bends: bendLabels.size, swaps: swapLabels.size, tier: analysis?.tier ?? null },
    });
  }, [applied, bendLabels, swapLabels, analysis]);

  // --- telemetry + the §7.2 deep link --------------------------------------
  useEffect(() => {
    if (tab !== 'map') return;
    logEvent({
      session_id: sessionId.current,
      event_type: 'bag_map_viewed',
      metadata: { focus: focus ?? null },
    });
  }, [tab, focus]);

  useEffect(() => {
    if (tab !== 'map' || focus !== 'bends') return;
    if (!findingsRef.current) return;
    /* `behavior: 'smooth'` is a no-op wherever smooth scrolling is off — a
       reduced-motion setting, some embedded contexts — and the deep link then
       silently lands at the top, which is the one thing §7.2's second door
       exists to avoid. A deep link should arrive, not animate. */
    findingsRef.current.scrollIntoView({ block: 'start' });
  }, [tab, focus, analysis]);

  // --- render --------------------------------------------------------------

  const tabButton = (key: TabKey, label: string) => {
    const active = tab === key;
    return (
      <button
        key={key}
        onClick={() => goToTab(key)}
        className={`px-4 py-2 text-sm font-semibold rounded-brand transition-colors ${
          active ? 'bg-green-ink text-white' : 'text-ink-2 hover:bg-paper'
        }`}
        style={{ minHeight: '44px' }}
      >
        {label}
      </button>
    );
  };

  const hasLadder = slots.length > 0;

  const analysisStatus = (
    <>
      {!analyzable ? (
        <p className="text-sm text-ink-2 mt-2">
          Enter at least two carries and the analysis starts here.
        </p>
      ) : analysisError ? (
        <p className="mt-2 text-sm text-score-0">{analysisError}</p>
      ) : analysis ? (
        <div className="mt-3">
          <AnalysisHeadline analysis={analysis} />
        </div>
      ) : (
        <p className="text-sm text-ink-2 mt-2">Waiting for the first analysis…</p>
      )}
    </>
  );

  const gapMapView = (
    <div className="space-y-5">
      <div className="bg-paper border border-line rounded-brand shadow-raised p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink">Gap Map</h2>
          {analyzing && <span className="text-sm text-ink-3">Recalculating…</span>}
        </div>

        {activeAnalysis ? (
          <>
            <p className="text-base text-ink mt-2">{verdict}</p>
            <div className="mt-3">
              <AnalysisHeadline analysis={activeAnalysis} />
            </div>
          </>
        ) : (
          analysisStatus
        )}

        {/* §4's before/after flip. */}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-3">
          <button
            type="button"
            onClick={handleToggleApply}
            disabled={!canApply}
            className={`px-4 py-2 text-sm font-semibold rounded-brand disabled:opacity-60 ${
              applied ? 'bg-green-ink text-white' : 'border border-line-2 text-ink-2 hover:bg-ground'
            }`}
            style={{ minHeight: '44px' }}
            aria-pressed={applied}
          >
            {applied ? 'Showing the bag with fixes applied' : 'Apply recommendations'}
          </button>
          <span className="text-sm text-ink-2">
            {!canApply
              ? 'Nothing to apply — no bend or swap cleared its tolerance.'
              : applied
              ? `${bendLabels.size} bend${bendLabels.size === 1 ? '' : 's'} applied${
                  swapLabels.size > 0
                    ? `, ${swapLabels.size} swap${swapLabels.size === 1 ? '' : 's'} marked`
                    : ''
                }. Nothing has been saved.`
              : 'See what the bag looks like with the fixes below in it.'}
          </span>
        </div>

        {applied && swapLabels.size > 0 && (
          <p className="mt-2 text-sm text-ink-3">
            A swap moves no rung: we know the club it replaces, not the carry the new one would
            have. Those rungs are marked, not moved.
          </p>
        )}
        {afterError && <p className="mt-2 text-sm text-score-0">{afterError}</p>}
      </div>

      <GapMap model={mapModel} applied={applied} onUnbench={handleUnbench} />

      <div ref={findingsRef} className="space-y-5">
        {analysis && analysis.findings.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
              Findings, in priority order
            </h3>
            <p className="text-sm text-ink-2">
              Bends come before purchases: a 2° bend is cheap and reversible, a new club is
              neither.
            </p>
            {analysis.findings.map((f, i) => (
              <FindingCard key={`${f.kind}-${i}`} f={f} />
            ))}
          </div>
        )}

        {activeAnalysis && activeAnalysis.holes.length + activeAnalysis.overlaps.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
              Gaps and overlaps{applied ? ' — what is left after the fixes' : ''}
            </h3>
            {activeAnalysis.holes.map((h, i) => (
              <GapCard key={`hole-${i}`} f={h} />
            ))}
            {activeAnalysis.overlaps.map((o, i) => (
              <GapCard key={`overlap-${i}`} f={o} />
            ))}
          </div>
        )}

        {analysis && analysis.blocked_bends.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
              Bends we are holding back
            </h3>
            <p className="text-sm text-ink-2">
              A bend is paid and irreversible, so we will not advise one off a number we invented
              for you.
            </p>
            {analysis.blocked_bends.map((b, i) => (
              <BlockedBendCard key={`${b.club_label}-${i}`} b={b} />
            ))}
          </div>
        )}

        {analysis &&
          analysis.findings.length === 0 &&
          analysis.holes.length === 0 &&
          analysis.overlaps.length === 0 &&
          analysis.blocked_bends.length === 0 && (
            <div className="bg-paper border border-line rounded-brand shadow-raised p-4">
              <p className="text-base text-ink">
                Nothing to flag on the numbers you have given us.
              </p>
              <p className="text-sm text-ink-2 mt-1">
                That is not the same as &quot;your bag is perfect&quot; — at tier {analysis.tier} it
                means nothing cleared the tolerance for the data we hold.
              </p>
            </div>
          )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ground">
      <ProfessionalHeader user={user} onLogout={logout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-ink font-display">My Bag</h1>
          <p className="text-sm text-ink-2 mt-1">
            Fourteen slots, long to short — the putter is one of them. One number per club:
            your stock, solid-strike carry, not your best one.
          </p>
        </div>

        <div className="flex gap-2 mb-5 border-b border-line pb-3">
          {tabButton('rack', 'Rack')}
          {tabButton('map', 'Gap Map')}
        </div>

        {error && (
          <div className="mb-5 bg-score-0-bg border border-line text-score-0 rounded-brand px-4 py-3">
            <p>{error}</p>
            <button onClick={load} className="mt-2 text-sm font-semibold underline">
              Try again
            </button>
          </div>
        )}

        {loading ? (
          <div className="bg-paper border border-line rounded-brand shadow-raised p-10 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mx-auto" />
            <p className="mt-4 text-ink-2">Loading your bag…</p>
          </div>
        ) : !hasLadder ? (
          tab === 'map' ? (
            <div className="bg-paper border border-line rounded-brand shadow-raised p-6">
              <h2 className="text-lg font-semibold text-ink">There is no bag to draw yet</h2>
              <p className="text-sm text-ink-2 mt-2">
                The Gap Map draws the ladder the Rack holds. Build one there first — we can
                propose the whole thing from your driver and 7-iron carries.
              </p>
              <button
                onClick={() => goToTab('rack')}
                className="mt-4 px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white"
                style={{ minHeight: '44px' }}
              >
                Go to the Rack
              </button>
            </div>
          ) : (
            <div className="bg-paper border border-line rounded-brand shadow-raised p-6">
              <h2 className="text-lg font-semibold text-ink">You have no bag yet</h2>
              <p className="text-sm text-ink-2 mt-2">
                Typing fourteen numbers is why a tool like this does not get used. We can
                propose the whole ladder from your driver and 7-iron carries — then you confirm
                each rung with one tap, or correct it.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  onClick={handleSeed}
                  disabled={seeding}
                  className="px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white disabled:opacity-60"
                  style={{ minHeight: '44px' }}
                >
                  {seeding ? 'Seeding…' : 'Seed from my profile'}
                </button>
                <button
                  onClick={handleStartByHand}
                  className="px-4 py-2 text-sm font-semibold rounded-brand border border-line-2 text-ink-2 hover:bg-ground"
                  style={{ minHeight: '44px' }}
                >
                  Fill it in by hand
                </button>
              </div>
              {seedReason && (
                <div className="mt-4 bg-score-1-bg border border-line text-score-1 rounded-brand px-4 py-3">
                  <p className="font-semibold text-sm">We could not seed from your profile</p>
                  <p className="text-sm mt-1">{seedReason}</p>
                  <p className="text-sm mt-2 text-ink-2">
                    That is a normal answer, not a failure. Fill the ladder in by hand and
                    everything below still works.
                  </p>
                </div>
              )}
            </div>
          )
        ) : tab === 'map' ? (
          gapMapView
        ) : (
          <div className="space-y-5">
            {pendingMeasured.length > 0 && (
              <div className="bg-paper border border-line rounded-brand shadow-raised px-4 py-3">
                <p className="text-sm text-ink">
                  A launch monitor has seen{' '}
                  <span className="font-semibold">
                    {pendingMeasured.length} of your clubs
                  </span>{' '}
                  — {pendingMeasured.map((c) => `${c.club_label} ${yards(c.carry_yards)}`).join(', ')}.
                  Each is the median of {measured?.min_shots ?? 3} or more shots from one session,
                  and it outranks anything typed in.
                </p>
                <button
                  onClick={handleApplyMeasured}
                  className="mt-2 px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white"
                  style={{ minHeight: '44px' }}
                >
                  Use my measured carries
                </button>
              </div>
            )}

            {estimatedCount > 0 && (
              <div className="bg-cyan-ink/10 border border-line rounded-brand px-4 py-3">
                <p className="text-sm text-ink">
                  {estimatedCount} rung{estimatedCount === 1 ? ' is' : 's are'} still{' '}
                  <span className="font-semibold">estimated</span>
                  {anchorsUsed.length > 0 && ` — seeded from your ${anchorsUsed.join(' and ')}`}.
                  An estimated number never drives a bend recommendation. Tap to confirm it,
                  or correct it.
                </p>
                <button
                  onClick={handleConfirmAll}
                  className="mt-2 px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white"
                  style={{ minHeight: '44px' }}
                >
                  These all look right
                </button>
              </div>
            )}

            <Rack
              slots={slots}
              onSlotChange={handleSlotChange}
              onConfirm={handleConfirm}
              onConfirmAll={handleConfirmAll}
              busy={saving}
              distanceBasis={bag?.distance_basis ?? 'carry'}
            />

            <div className="bg-paper border border-line rounded-brand shadow-raised px-4 py-3 flex flex-wrap items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white disabled:opacity-60"
                style={{ minHeight: '44px' }}
              >
                {saving ? 'Saving…' : bag === null ? 'Save my bag' : 'Save changes'}
              </button>
              <span className="text-sm text-ink-2">
                {saving
                  ? 'Writing the whole ladder in one transaction…'
                  : dirty
                  ? 'Unsaved changes.'
                  : savedAt
                  ? `Saved at ${savedAt}.`
                  : 'Everything is saved.'}
              </span>
            </div>

            {saveError && (
              <div className="bg-score-0-bg border border-line text-score-0 rounded-brand px-4 py-3">
                {saveError}
              </div>
            )}

            {/* The Rack says what the bag adds up to; the map is where the
                advice lives, so the findings are one tap away rather than
                duplicated here (§7.2). */}
            <div className="bg-paper border border-line rounded-brand shadow-raised p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-ink">What your bag says</h2>
                {analyzing && <span className="text-sm text-ink-3">Recalculating…</span>}
              </div>

              {analysisStatus}

              {analysis && (
                <>
                  <p className="text-base text-ink mt-3">{verdict}</p>
                  <p className="text-sm text-ink-2 mt-1">
                    {analysis.findings.length === 0
                      ? 'Nothing cleared its tolerance, so there is no finding to act on.'
                      : `${analysis.findings.length} finding${
                          analysis.findings.length === 1 ? '' : 's'
                        }, bends first.`}
                    {analysis.blocked_bends.length > 0 &&
                      ` ${analysis.blocked_bends.length} bend${
                        analysis.blocked_bends.length === 1 ? ' is' : 's are'
                      } being held back — confirm the rungs around them and they become advice.`}
                  </p>
                  <button
                    onClick={() => goToTab('map')}
                    className="mt-3 px-4 py-2 text-sm font-semibold rounded-brand bg-green-ink text-white"
                    style={{ minHeight: '44px' }}
                  >
                    See the Gap Map
                  </button>
                  <p className="text-sm text-ink-3 mt-3">{LONG_IRON_CAVEAT}</p>
                  {analysis.target_gap !== null && (
                    <p className="text-sm text-ink-3 mt-1">
                      Target gap {yards(analysis.target_gap)} — every gap on the map is judged
                      against it.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBagPage;
